import { ActivatedRouteSnapshot, Routes } from '@angular/router';

// Import components as they are migrated
import { UserViewComponent } from '@fry/upgrade/users.directive';
import { ListMfasComponent } from '@fry/system/account/mfa/list-mfas.component';
import { ListCredentialsComponent } from '@fry/system/account/credential/list-credentials.component';
import { UserEditComponent } from '@fry/upgrade/users.directive';
import { TwoColumnsRightComponent } from '@fry/upgrade/main.template.directive';
import { UsersListComponent } from '@fry/upgrade/users.directive';
import { UserCreateComponent } from '@fry/upgrade/users.directive';
import { UserEventsComponent } from '@fry/upgrade/users.directive';
import { EventSectionViewComponent, EventViewComponent } from '@fry/upgrade/events.directive';
import { FilesListComponent } from '@fry/upgrade/files.directive';
import { GoalEditComponent, GoalViewComponent, UserGoalsComponent } from '@fry/upgrade/goals.directive';
import { PreRegisterApiCredentialComponent } from '@fry/system/account/credential/pre-register-api-credential.component';
import { inject } from '@angular/core';
import { SecurityService } from '@fry-it/risr-core';
import { setupGuard } from '@fry/upgrade/setup.guard';
import { authGuard } from '@fry-it/risr-core';
import { forceOnlineGuard } from '@fry-it/risr-core';
import { unsavedGuard } from '@fry/upgrade/unsaved.guard';

export const usersRoutes: Routes = [
  // Dashboard profile routes
  {
    path: 'profile',
    canActivate: [setupGuard],
    children: [
      {
        path: 'view',
        component: UserViewComponent,
        data: {
          angularJsTemplate: 'app/components/users/view.html',
          angularJsController: 'UserViewController',
          angularJsControllerAs: 'userViewCtrl',
          state: 'dashboard.profile.view'
        }
      },
      {
        path: 'view/:user',
        component: UserViewComponent,
        data: {
          angularJsTemplate: 'app/components/users/view.html',
          angularJsController: 'UserViewController',
          angularJsControllerAs: 'userViewCtrl',
          state: 'dashboard.profile.view'
        }
      },
      {
        path: 'edit',
        component: UserEditComponent,
        data: {
          angularJsTemplate: 'app/components/users/edit.html',
          angularJsController: 'UserEditController',
          angularJsControllerAs: 'userEditCtrl',
          state: 'dashboard.profile.edit',
          permission: 'users.edit.own'
        },
        canActivate: [authGuard, forceOnlineGuard],
        canDeactivate: [unsavedGuard],
      },
      {
        path: 'edit/:user',
        component: UserEditComponent,
        data: {
          angularJsTemplate: 'app/components/users/edit.html',
          angularJsController: 'UserEditController',
          angularJsControllerAs: 'userEditCtrl',
          state: 'dashboard.profile.edit'
        },
        canActivate: [
          async (route: ActivatedRouteSnapshot) => {
            const security = inject(SecurityService);
            const res =  await security.hasOwnPermissionFor('users.edit', route.params['user']);
            return res;
          },
          forceOnlineGuard
        ],
        canDeactivate: [unsavedGuard],
      },
      {
        path: 'edit/:user/:goTo',
        component: UserEditComponent,
        data: {
          angularJsTemplate: 'app/components/users/edit.html',
          angularJsController: 'UserEditController',
          angularJsControllerAs: 'userEditCtrl',
          state: 'dashboard.profile.edit'
        },
        canActivate: [
          async (route: ActivatedRouteSnapshot) => {
            const security = inject(SecurityService);
            const res =  await security.hasOwnPermissionFor('users.edit', route.params['user']);
            return res;
          },
          forceOnlineGuard
        ],
        canDeactivate: [unsavedGuard],
      },
      {
        path: 'security/credentials',
        component: ListCredentialsComponent,
        canActivate: [forceOnlineGuard]
      },
      {
        path: 'security/credentials/:credentialUsername/mfas',
        component: ListMfasComponent,
        canActivate: [forceOnlineGuard]
      }
    ]
  },

  // Main users routes
  {
    path: 'users',
    component: TwoColumnsRightComponent,
    data: {
      permission: 'users.view'
    },
    canActivate: [setupGuard, forceOnlineGuard, authGuard],
    children: [
      {
        path: '',
        component: UsersListComponent,
        data: {
          angularJsTemplate: 'app/components/users/list.html',
          angularJsController: 'UsersController',
          angularJsControllerAs: 'usersCtrl',
          action: 'view',
          state: 'epf.users.index'
        },
        canActivate: [forceOnlineGuard]
      },
      {
        path: 'new',
        component: UserCreateComponent,
        data: {
          angularJsTemplate: 'app/components/users/new.html',
          angularJsController: 'UserCreateController',
          angularJsControllerAs: 'userCreateCtrl',
          action: 'new',
          state: 'epf.users.new',
          permission: 'users.create'
        },
        canActivate: [forceOnlineGuard, authGuard],
        canDeactivate: [unsavedGuard],
      },
      {
        path: 'security/:user/credentials',
        component: ListCredentialsComponent,
        data: {
          permission: 'credential.manage'
        },
        canActivate: [forceOnlineGuard]
      },
      {
        path: 'security/:user/credentials/:credentialUsername/mfas',
        component: ListMfasComponent,
        data: {
          permission: 'credential.manage'
        },
        canActivate: [forceOnlineGuard, authGuard]
      },
      {
        path: 'security/credentials/pre-register-api-credential',
        component: PreRegisterApiCredentialComponent,
        canActivate: [forceOnlineGuard]
      },
      {
        path: 'edit/:user/events/list',
        component: UserEventsComponent,
        data: {
          angularJsTemplate: 'app/components/users/eventlist.html',
          angularJsController: 'UserEventsController',
          angularJsControllerAs: 'eventsCtrl',
          state: 'epf.users.events',
          permission: 'events.view'
        },
        canActivate: [forceOnlineGuard, authGuard]
      },
      {
        path: 'edit/:user/events/list/:filterId',
        component: UserEventsComponent,
        data: {
          angularJsTemplate: 'app/components/users/eventlist.html',
          angularJsController: 'UserEventsController',
          angularJsControllerAs: 'eventsCtrl',
          state: 'epf.users.events',
          permission: 'events.view'
        },
        canActivate: [forceOnlineGuard, authGuard]
      },
      {
        path: 'edit/:user/files',
        component: FilesListComponent,
        data: {
          angularJsTemplate: 'app/components/files/files.html',
          angularJsController: 'FilesController',
          angularJsControllerAs: 'filesCtrl',
          state: 'epf.users.files',
          permission: 'events.view'
        },
        canActivate: [forceOnlineGuard, authGuard]
      },
      {
        path: 'edit/:user/goals',
        component: UserGoalsComponent,
        data: {
          angularJsTemplate: 'app/components/users/goalslist.html',
          angularJsController: 'UserGoalsController',
          angularJsControllerAs: 'goalsCtrl',
          state: 'epf.users.goals',
          permission: 'goals.view'
        },
        canActivate: [forceOnlineGuard, authGuard]
      },
      {
        path: 'edit/:user/reports',
        loadChildren: () => import('@fry-it/risr-features/reports').then(m => m.reportRoutes as Routes),
        canActivate: [forceOnlineGuard, authGuard]
      },
      {
        path: 'edit/:user/goals/:filterId',
        component: UserGoalsComponent,
        data: {
          angularJsTemplate: 'app/components/users/goalslist.html',
          angularJsController: 'UserGoalsController',
          angularJsControllerAs: 'goalsCtrl',
          state: 'epf.users.goals',
          permission: 'goals.view'
        },
        canActivate: [forceOnlineGuard, authGuard]
      },
      {
        path: 'add/:user/goals/:eventId/:sectionId/:fieldId',
        component: GoalEditComponent,
        canDeactivate: [unsavedGuard],
        data: {
          angularJsTemplate: 'app/components/goals/goals-definition.html',
          angularJsController: 'GoalsDefinitionController',
          angularJsControllerAs: 'GoalsDefinitionCtrl',
          action: 'add',
          state: 'epf.users.goals-add'
        }
      },
      {
        path: 'edit-definition/:user/goals/:goalId',
        component: GoalEditComponent,
        canDeactivate: [unsavedGuard],
        data: {
          angularJsTemplate: 'app/components/goals/goals-definition.html',
          angularJsController: 'GoalsDefinitionController',
          angularJsControllerAs: 'GoalsDefinitionCtrl',
          action: 'edit',
          state: 'epf.users.goals-editDefinition'
        }
      },
      {
        path: 'view/:user/goals/:goalId',
        component: GoalViewComponent,
        data: {
          angularJsTemplate: 'app/components/goals/goal.html',
          angularJsController: 'GoalController',
          angularJsControllerAs: 'goalCtrl',
          action: 'view',
          state: 'epf.users.goals-view'
        }
      },
      {
        path: 'work/:user/goals/:goalId',
        component: GoalViewComponent,
        data: {
          angularJsTemplate: 'app/components/goals/goal.html',
          angularJsController: 'GoalController',
          angularJsControllerAs: 'goalCtrl',
          action: 'work',
          state: 'epf.users.goals-work'
        }
      },
      {
        path: 'work/:user/targets/link/:goalId/:targetId',
        component: GoalViewComponent,
        data: {
          angularJsTemplate: 'app/components/goals/targets.link.html',
          angularJsController: 'TargetsLinkController',
          angularJsControllerAs: 'targetsLinkController',
          action: 'work',
          state: 'epf.users.goals-link'
        }
      },
      {
        path: 'edit/events/:eventId',
        component: EventViewComponent,
        data: {
          angularJsTemplate: 'app/components/events/view.html',
          angularJsController: 'EventViewController',
          angularJsControllerAs: 'eventViewCtrl',
          action: 'redirect',
          state: 'epf.users.events-view'
        },
        canActivate: [forceOnlineGuard]
      },
      {
        path: 'edit/:user/events/:eventId',
        component: EventViewComponent,
        data: {
          angularJsTemplate: 'app/components/events/view.html',
          angularJsController: 'EventViewController',
          angularJsControllerAs: 'eventViewCtrl',
          action: 'view',
          state: 'epf.users.events-view'
        },
        canActivate: [forceOnlineGuard],
        canDeactivate: [unsavedGuard],
      },
      {
        path: 'edit/:user/events/:eventId/:scrollTo',
        component: EventViewComponent,
        data: {
          angularJsTemplate: 'app/components/events/view.html',
          angularJsController: 'EventViewController',
          angularJsControllerAs: 'eventViewCtrl',
          action: 'view',
          state: 'epf.users.events-view'
        },
        canActivate: [forceOnlineGuard],
        canDeactivate: [unsavedGuard],
      },
      {
        path: 'edit/:user/sections/:eventSectionId',
        component: EventSectionViewComponent,
        data: {
          angularJsTemplate: 'app/components/events/eventsection.view.html',
          angularJsController: 'EventSectionViewController',
          angularJsControllerAs: 'eventViewCtrl',
          action: 'view',
          state: 'epf.users.sections-view'
        },
        canActivate: [forceOnlineGuard],
        canDeactivate: [unsavedGuard],
      },
      {
        path: 'edit/:user/sections/:eventSectionId/:scrollTo',
        component: EventSectionViewComponent,
        data: {
          angularJsTemplate: 'app/components/events/eventsection.view.html',
          angularJsController: 'EventSectionViewController',
          angularJsControllerAs: 'eventViewCtrl',
          action: 'view',
          state: 'epf.users.sections-view'
        },
        canActivate: [forceOnlineGuard],
        canDeactivate: [unsavedGuard],
      }
    ]
  }
];
